import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { format, startOfMonth, endOfMonth, startOfWeek, endOfWeek, eachDayOfInterval, isSameDay, isSameMonth, parseISO } from 'date-fns';
import { ChevronLeft, ChevronRight, CheckSquare, Video, UserCog, Shield, Loader2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EVENT_TYPES = {
  task: { label: 'Task', color: '#007AFF', bg: 'bg-[#007AFF]', light: 'bg-[#007AFF]/10 text-[#007AFF]', icon: CheckSquare },
  task_critical: { label: 'Critical', color: '#FF3B30', bg: 'bg-[#FF3B30]', light: 'bg-[#FF3B30]/10 text-[#FF3B30]', icon: CheckSquare },
  task_high: { label: 'High', color: '#FF9500', bg: 'bg-[#FF9500]', light: 'bg-[#FF9500]/10 text-[#FF9500]', icon: CheckSquare },
  webinar: { label: 'Webinar', color: '#FF2D55', bg: 'bg-[#FF2D55]', light: 'bg-[#FF2D55]/10 text-[#FF2D55]', icon: Video },
  hr: { label: 'HR', color: '#AF52DE', bg: 'bg-[#AF52DE]', light: 'bg-[#AF52DE]/10 text-[#AF52DE]', icon: UserCog },
  audit: { label: 'Audit', color: '#5856D6', bg: 'bg-[#5856D6]', light: 'bg-[#5856D6]/10 text-[#5856D6]', icon: Shield },
};

const CalendarPage = () => {
  const { token } = useAuth();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [view, setView] = useState('month');
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [filters, setFilters] = useState({ task: true, webinar: true, hr: true, audit: true });

  useEffect(() => { fetchAllEvents(); }, []);

  const fetchAllEvents = async () => {
    setLoading(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const [tasksRes, webinarsRes, hrRes, auditRes] = await Promise.all([
        axios.get(`${API}/tasks`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${API}/webinars`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${API}/hr/employees`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${API}/knowledge-audit/schedule`, { headers }).catch(() => ({ data: {} })),
      ]);

      const allEvents = [];

      // Tasks with due dates
      (tasksRes.data || []).forEach(t => {
        if (!t.due_date || t.status === 'completed') return;
        const eventType = t.priority === 'critical' ? 'task_critical' : t.priority === 'high' ? 'task_high' : 'task';
        allEvents.push({
          id: t.task_id, title: t.title, date: t.due_date, type: eventType,
          detail: t.description || '', status: t.status, priority: t.priority, link: '/tasks',
        });
      });

      // Webinars
      (webinarsRes.data || []).forEach(w => {
        allEvents.push({
          id: w.webinar_id, title: w.title, date: w.scheduled_at, type: 'webinar',
          detail: `${w.duration_minutes || 60}min · ${w.attendees?.length || 0} attendees`,
          status: w.status, link: '/webinars',
        });
        // Include recurring occurrences
        (w.occurrences || []).forEach((occ, i) => {
          allEvents.push({
            id: `${w.webinar_id}_occ_${i}`, title: `${w.title} (recurring)`, date: occ, type: 'webinar',
            detail: `${w.duration_minutes || 60}min`, status: 'scheduled', link: '/webinars',
          });
        });
      });

      // HR Workflows (onboarding/offboarding dates)
      (hrRes.data || []).forEach(emp => {
        if (emp.start_date) {
          allEvents.push({
            id: `hr_start_${emp.employee_id}`, title: `Onboarding: ${emp.name}`, date: emp.start_date, type: 'hr',
            detail: emp.department || '', status: 'scheduled', link: '/hr-workflows',
          });
        }
        if (emp.end_date) {
          allEvents.push({
            id: `hr_end_${emp.employee_id}`, title: `Offboarding: ${emp.name}`, date: emp.end_date, type: 'hr',
            detail: emp.department || '', status: 'scheduled', link: '/hr-workflows',
          });
        }
      });

      // Audit schedules
      const schedule = auditRes.data;
      if (schedule?.next_run) {
        allEvents.push({
          id: 'audit_next', title: 'Knowledge Audit', date: schedule.next_run, type: 'audit',
          detail: `Scheduled ${schedule.frequency || 'weekly'} audit`, status: 'scheduled', link: '/knowledge-audit',
        });
      }

      setEvents(allEvents);
    } catch { /* silent */ }
    finally { setLoading(false); }
  };

  const getEventsForDate = (date) => {
    return events.filter(ev => {
      if (!ev.date) return false;
      const typeBase = ev.type.startsWith('task') ? 'task' : ev.type;
      if (!filters[typeBase]) return false;
      try { return isSameDay(parseISO(ev.date), date); } catch { return false; }
    });
  };

  const toggleFilter = (key) => setFilters(prev => ({ ...prev, [key]: !prev[key] }));

  const monthStart = startOfMonth(currentDate);
  const monthEnd = endOfMonth(currentDate);
  const calendarStart = startOfWeek(monthStart);
  const calendarEnd = endOfWeek(monthEnd);
  const calendarDays = eachDayOfInterval({ start: calendarStart, end: calendarEnd });

  const navigateMonth = (dir) => {
    const d = new Date(currentDate);
    d.setMonth(d.getMonth() + dir);
    setCurrentDate(d);
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64" data-testid="calendar-loading"><Loader2 className="w-8 h-8 animate-spin text-[#002FA7]" /></div>;
  }

  // Summary counts
  const totalTasks = events.filter(e => e.type.startsWith('task')).length;
  const totalWebinars = events.filter(e => e.type === 'webinar').length;
  const totalHR = events.filter(e => e.type === 'hr').length;

  return (
    <div data-testid="calendar-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-semibold text-[#0A0A0A]">Calendar</h1>
          <p className="text-[#4B5563] text-sm">Unified view of tasks, webinars, HR events, and audits</p>
        </div>
        <div className="flex items-center gap-2 text-xs">
          <span className="text-[#9CA3AF]">{events.length} events</span>
        </div>
      </div>

      {/* Filters & Legend */}
      <Card className="border-[#E5E7EB] mb-4">
        <CardContent className="p-3">
          <div className="flex flex-wrap items-center gap-3">
            {[
              { key: 'task', label: `Tasks (${totalTasks})`, color: '#007AFF' },
              { key: 'webinar', label: `Webinars (${totalWebinars})`, color: '#FF2D55' },
              { key: 'hr', label: `HR (${totalHR})`, color: '#AF52DE' },
              { key: 'audit', label: 'Audits', color: '#5856D6' },
            ].map(f => (
              <button
                key={f.key}
                onClick={() => toggleFilter(f.key)}
                className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium transition-all ${filters[f.key] ? 'text-white' : 'text-[#9CA3AF] bg-[#F3F4F6]'}`}
                style={filters[f.key] ? { backgroundColor: f.color } : {}}
                data-testid={`filter-${f.key}`}
              >
                <div className={`w-2 h-2 rounded-full ${filters[f.key] ? 'bg-white/60' : ''}`} style={!filters[f.key] ? { backgroundColor: f.color } : {}} />
                {f.label}
              </button>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Calendar */}
      <Card className="border-[#E5E7EB]">
        <CardHeader className="border-b border-[#E5E7EB] py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => navigateMonth(-1)} data-testid="prev-month-btn"><ChevronLeft className="w-4 h-4" /></Button>
              <h2 className="text-base font-semibold min-w-[160px] text-center">{format(currentDate, 'MMMM yyyy')}</h2>
              <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => navigateMonth(1)} data-testid="next-month-btn"><ChevronRight className="w-4 h-4" /></Button>
              <Button variant="outline" size="sm" className="h-7 text-xs" onClick={() => { setCurrentDate(new Date()); setSelectedDate(new Date()); }} data-testid="today-btn">Today</Button>
            </div>
            <Tabs value={view} onValueChange={setView}>
              <TabsList className="h-8">
                <TabsTrigger value="month" className="text-xs h-7" data-testid="month-view-tab">Month</TabsTrigger>
                <TabsTrigger value="week" className="text-xs h-7" data-testid="week-view-tab">Week</TabsTrigger>
                <TabsTrigger value="day" className="text-xs h-7" data-testid="day-view-tab">Day</TabsTrigger>
              </TabsList>
            </Tabs>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          {view === 'month' && (
            <div className="grid grid-cols-7" data-testid="calendar-month-view">
              {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(d => (
                <div key={d} className="p-2 text-center text-xs font-medium text-[#9CA3AF] border-b border-[#E5E7EB]">{d}</div>
              ))}
              {calendarDays.map((day, i) => {
                const dayEvents = getEventsForDate(day);
                const isToday = isSameDay(day, new Date());
                const isSelected = isSameDay(day, selectedDate);
                const inMonth = isSameMonth(day, currentDate);
                return (
                  <div
                    key={i}
                    className={`min-h-[90px] p-1 border-b border-r border-[#E5E7EB] cursor-pointer transition-colors ${!inMonth ? 'bg-[#F9FAFB] opacity-40' : isSelected ? 'bg-[#002FA7]/5' : 'hover:bg-[#F9FAFB]'}`}
                    onClick={() => { setSelectedDate(day); setView('day'); }}
                    data-testid={`calendar-day-${format(day, 'yyyy-MM-dd')}`}
                  >
                    <div className={`text-xs font-medium mb-0.5 w-6 h-6 flex items-center justify-center ${isToday ? 'bg-[#002FA7] text-white rounded-full' : 'text-[#4B5563]'}`}>
                      {format(day, 'd')}
                    </div>
                    <div className="space-y-0.5">
                      {dayEvents.slice(0, 3).map((ev, j) => {
                        const cfg = EVENT_TYPES[ev.type] || EVENT_TYPES.task;
                        return (
                          <div key={j} className="text-[10px] px-1 py-0.5 rounded truncate text-white" style={{ backgroundColor: cfg.color }} title={ev.title}>
                            {ev.title}
                          </div>
                        );
                      })}
                      {dayEvents.length > 3 && <div className="text-[10px] text-[#9CA3AF] pl-1">+{dayEvents.length - 3} more</div>}
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {view === 'week' && (() => {
            const weekStart = startOfWeek(currentDate);
            const weekDays = eachDayOfInterval({ start: weekStart, end: endOfWeek(currentDate) });
            return (
              <div className="grid grid-cols-7" data-testid="calendar-week-view">
                {weekDays.map((day, i) => {
                  const dayEvents = getEventsForDate(day);
                  const isToday = isSameDay(day, new Date());
                  return (
                    <div key={i} className="border-r border-[#E5E7EB] last:border-r-0">
                      <div className={`p-2 text-center border-b border-[#E5E7EB] ${isToday ? 'bg-[#002FA7] text-white' : 'bg-[#F9FAFB]'}`}>
                        <div className="text-[10px] uppercase">{format(day, 'EEE')}</div>
                        <div className="text-lg font-semibold">{format(day, 'd')}</div>
                      </div>
                      <div className="p-1.5 space-y-1 min-h-[250px]">
                        {dayEvents.map((ev, j) => {
                          const cfg = EVENT_TYPES[ev.type] || EVENT_TYPES.task;
                          const Icon = cfg.icon;
                          return (
                            <Link key={j} to={ev.link}>
                              <div className={`text-[10px] p-1.5 rounded border-l-2 bg-[#F9FAFB] hover:bg-[#F3F4F6] transition-colors`} style={{ borderLeftColor: cfg.color }}>
                                <div className="flex items-center gap-1">
                                  <Icon className="w-2.5 h-2.5 flex-shrink-0" style={{ color: cfg.color }} />
                                  <span className="font-medium truncate">{ev.title}</span>
                                </div>
                                {ev.detail && <p className="text-[#9CA3AF] truncate mt-0.5">{ev.detail}</p>}
                              </div>
                            </Link>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>
            );
          })()}

          {view === 'day' && (() => {
            const dayEvents = getEventsForDate(selectedDate);
            return (
              <div className="p-4" data-testid="calendar-day-view">
                <div className="text-center mb-6">
                  <h3 className="text-lg font-semibold">{format(selectedDate, 'EEEE, MMMM d, yyyy')}</h3>
                  <p className="text-xs text-[#9CA3AF] mt-1">{dayEvents.length} event{dayEvents.length !== 1 ? 's' : ''}</p>
                </div>
                {dayEvents.length === 0 ? (
                  <div className="text-center py-12 text-[#9CA3AF]">
                    <p className="text-sm">No events scheduled for this day</p>
                  </div>
                ) : (
                  <div className="space-y-2 max-w-2xl mx-auto">
                    {dayEvents.map((ev, i) => {
                      const cfg = EVENT_TYPES[ev.type] || EVENT_TYPES.task;
                      const Icon = cfg.icon;
                      return (
                        <Link key={i} to={ev.link}>
                          <Card className="border-[#E5E7EB] hover:border-[#002FA7]/30 transition-all hover:shadow-sm" data-testid={`day-event-${i}`}>
                            <CardContent className="p-3 flex items-center gap-3">
                              <div className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0" style={{ backgroundColor: cfg.color + '15' }}>
                                <Icon className="w-4 h-4" style={{ color: cfg.color }} />
                              </div>
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2">
                                  <h4 className="text-sm font-medium text-[#0A0A0A] truncate">{ev.title}</h4>
                                  <Badge className={cfg.light + ' text-[9px]'}>{cfg.label}</Badge>
                                </div>
                                {ev.detail && <p className="text-xs text-[#9CA3AF] mt-0.5">{ev.detail}</p>}
                              </div>
                              {ev.status && <Badge variant="secondary" className="text-[9px]">{ev.status}</Badge>}
                            </CardContent>
                          </Card>
                        </Link>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })()}
        </CardContent>
      </Card>
    </div>
  );
};

export default CalendarPage;
